/**
 * src/pages/Deals.tsx
 *
 * Tab 2 — Deal Explorer. Filterable admin table over `deals`, click a
 * row → drawer with three sub-sections:
 *   - Deal record  (full doc + anchor breakdown JSON)
 *   - Pipeline log (every pipeline_logs row for this unique_id)
 *   - Deliveries   (every deliveries row for this deal_id)
 *
 * Plus the only write affordance in the dashboard: "flag wrong
 * decision" — appends to admin_decision_flags. Never mutates the deal.
 *
 * URL query params persist filters (?source=cex_uk&decision=DROP&q=…)
 * so an investigation link survives a page reload.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, Loader2, Shuffle, X as XIcon } from 'lucide-react';
import {
  getAdminDeals, sampleAdminDeals, getAdminDeal,
  getDealPipelineLog, getDealDeliveries,
  flagDeal,
  type AdminDecision, type AdminDealRow, type AdminLiveStatus,
  type AdminDealTier, type FlagReason,
} from '@/api/dbview';
import { cn } from '@/lib/cn';
import { relativeTime, num, txt } from '@/lib/format';

// 2026-05-10 — Tony calibration #4 dealTier emoji map. Mirrors the
// authoritative DEAL_TIER_EMOJI in `backend/product/src/modules/deals/deals.service.ts`.
// If you tune the bands or emoji set, update both files together.
const DEAL_TIER_EMOJI: Record<AdminDealTier, string> = {
  exceptional: '🔥',
  strong:      '✓',
  standard:    '•',
};

/** Bands match `deals.service.ts::dealTier()` (≥40 / 25-39 / <25). Returns
 *  null when discountPct is missing — callers render a neutral "—" cell. */
function dealTierFromDiscount(pct: number | null | undefined): AdminDealTier | null {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  if (pct >= 40) return 'exceptional';
  if (pct >= 25) return 'strong';
  return 'standard';
}

const FLAG_REASONS: FlagReason[] = [
  'wrong-DROP', 'wrong-HOLD', 'wrong-EMIT', 'wrong-score', 'wrong-category',
];

const DECISION_COLOR: Record<string, string> = {
  EMIT:     'text-emerald-400',
  HOLD:     'text-amber-400',
  DROP:     'text-rose-400',
  SUPPRESS: 'text-slate-500',
};

interface FilterState {
  source: string;
  category: string;
  decision: '' | AdminDecision;
  status:   '' | AdminLiveStatus;
  /** 2026-05-10 — Tony calibration #4 dealTier band filter. Server
   *  translates to a discountPct range. */
  dealTier: '' | AdminDealTier;
  q: string;
  minDcs: string;
  maxDcs: string;
  minPrice: string;
  maxPrice: string;
}

const EMPTY_FILTERS: FilterState = {
  source: '', category: '', decision: '', status: '', dealTier: '',
  q: '', minDcs: '', maxDcs: '', minPrice: '', maxPrice: '',
};

function readUrlFilters(): FilterState {
  const out: FilterState = { ...EMPTY_FILTERS };
  if (typeof window === 'undefined') return out;
  const params = new URLSearchParams(window.location.search);
  for (const k of Object.keys(out) as Array<keyof FilterState>) {
    const v = params.get(k);
    // Cast through unknown — FilterState's discriminated unions for
    // `decision` / `status` make a direct string assignment unsafe at
    // the type level, but every value reaching here came from the URL
    // (i.e. already string) and is filtered server-side anyway.
    if (v !== null) (out as unknown as Record<string, string>)[k] = v;
  }
  return out;
}

function writeUrlFilters(f: FilterState): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) params.set(k, v);
  const qs = params.toString();
  const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState({}, '', url);
}

function toApiFilter(f: FilterState) {
  return {
    source:   f.source   || undefined,
    category: f.category || undefined,
    decision: f.decision || undefined,
    status:   f.status   || undefined,
    dealTier: f.dealTier || undefined,
    q:        f.q        || undefined,
    minDcs:   f.minDcs   ? Number(f.minDcs)   : undefined,
    maxDcs:   f.maxDcs   ? Number(f.maxDcs)   : undefined,
    minPrice: f.minPrice ? Number(f.minPrice) : undefined,
    maxPrice: f.maxPrice ? Number(f.maxPrice) : undefined,
  };
}

export default function Deals() {
  const [filters, setFilters] = useState<FilterState>(readUrlFilters);
  const [openUid, setOpenUid] = useState<string | null>(null);
  const [sampling, setSampling] = useState(false);
  const [sampleRows, setSampleRows] = useState<AdminDealRow[] | null>(null);

  // Persist filters to URL (replaceState — no router event).
  useEffect(() => { writeUrlFilters(filters); }, [filters]);

  const apiFilter = useMemo(() => ({ ...toApiFilter(filters), limit: 100 }), [filters]);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['deals', apiFilter],
    queryFn:  () => getAdminDeals(apiFilter),
  });

  async function runSample() {
    setSampling(true);
    try {
      const r = await sampleAdminDeals({ ...toApiFilter(filters), n: 50 });
      setSampleRows(r.rows);
    } finally {
      setSampling(false);
    }
  }

  const rows = sampleRows ?? data?.rows ?? [];

  return (
    <div className="space-y-4 max-w-7xl">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold">Deals</h1>
          <p className="text-sm text-slate-500 mt-1">
            Admin filter over `deals` · click a row for pipeline log + deliveries
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runSample}
            className="px-2.5 py-1.5 text-xs rounded bg-slate-800 hover:bg-slate-700 text-white flex items-center gap-1"
            title="Random 50 of the current filter — for manual classification"
          >
            <Shuffle className="w-3.5 h-3.5" />
            sample 50
          </button>
          {sampleRows && (
            <button
              onClick={() => setSampleRows(null)}
              className="px-2.5 py-1.5 text-xs rounded text-slate-400 hover:text-white"
            >
              clear sample
            </button>
          )}
        </div>
      </header>

      <FilterBar filters={filters} setFilters={setFilters} />

      {sampling && <LoadingRow label="Sampling…" />}
      {isLoading && !sampling && <LoadingRow label="Loading deals…" />}
      {isError   && <ErrorRow   label="Failed to load deals." />}
      {data && !sampleRows && (
        <div className="text-xs text-slate-500">
          {data.total_is_estimate ? '~' : ''}{data.total.toLocaleString()} match · showing {rows.length}
        </div>
      )}
      {sampleRows && (
        <div className="text-xs text-amber-300">
          Showing random sample of {sampleRows.length} (sampled from current filter)
        </div>
      )}

      <DealsTable rows={rows} onOpen={setOpenUid} />

      {openUid && <DealDrawer uid={openUid} onClose={() => setOpenUid(null)} />}
    </div>
  );
}

function FilterBar({ filters, setFilters }: {
  filters: FilterState;
  setFilters: (f: FilterState) => void;
}) {
  function field(k: keyof FilterState, label: string, placeholder?: string) {
    return (
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        {label}
        <input
          value={filters[k] ?? ''}
          onChange={e => setFilters({ ...filters, [k]: e.target.value })}
          placeholder={placeholder}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm w-32 font-mono"
        />
      </label>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-2 bg-slate-900/40 border border-slate-800 rounded-md p-3">
      {field('source',   'source',   'cex_uk')}
      {field('category', 'category', 'phones')}
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        decision
        <select
          value={filters.decision}
          onChange={e => setFilters({ ...filters, decision: e.target.value as FilterState['decision'] })}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm"
        >
          <option value="">any</option>
          <option value="EMIT">EMIT</option>
          <option value="HOLD">HOLD</option>
          <option value="DROP">DROP</option>
          <option value="SUPPRESS">SUPPRESS</option>
        </select>
      </label>
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        status
        <select
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value as FilterState['status'] })}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm"
        >
          <option value="">any</option>
          <option value="live">live</option>
          <option value="expired">expired</option>
          <option value="suspended">suspended</option>
          <option value="oos">OOS</option>
        </select>
      </label>
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        tier
        <select
          value={filters.dealTier}
          onChange={e => setFilters({ ...filters, dealTier: e.target.value as FilterState['dealTier'] })}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm"
          title="dealTier band (Tony calibration #4) — derived from discountPct"
        >
          <option value="">any</option>
          <option value="exceptional">🔥 exceptional (≥40%)</option>
          <option value="strong">✓ strong (25–39%)</option>
          <option value="standard">• standard (&lt;25%)</option>
        </select>
      </label>
      <label className="text-xs text-slate-400 flex flex-col gap-1 flex-1 min-w-[10rem]">
        search
        <input
          value={filters.q}
          onChange={e => setFilters({ ...filters, q: e.target.value })}
          placeholder="title / unique_id / modelKey"
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"
        />
      </label>
      {field('minDcs',   'minDCS',   '0')}
      {field('maxDcs',   'maxDCS',   '100')}
      {field('minPrice', 'min£')}
      {field('maxPrice', 'max£')}
      <button
        onClick={() => setFilters(EMPTY_FILTERS)}
        className="px-2.5 py-1.5 text-xs rounded text-slate-400 hover:text-white self-end"
      >
        reset
      </button>
    </div>
  );
}

function DealsTable({ rows, onOpen }: { rows: AdminDealRow[]; onOpen: (uid: string) => void }) {
  if (rows.length === 0) {
    return <div className="text-sm text-slate-500 py-4 text-center">No deals match.</div>;
  }
  return (
    <div className="overflow-x-auto border border-slate-800 rounded">
      <table className="w-full text-xs">
        <thead className="bg-slate-900/80 text-slate-400">
          <tr>
            <th className="text-left  px-2 py-1.5">when</th>
            <th className="text-left  px-2 py-1.5">source</th>
            <th className="text-left  px-2 py-1.5">title</th>
            <th className="text-right px-2 py-1.5">£</th>
            <th className="text-center px-2 py-1.5" title="dealTier — 🔥≥40 ✓25–39 •<25">tier</th>
            <th className="text-right px-2 py-1.5">DCS</th>
            <th className="text-left  px-2 py-1.5">decision</th>
            <th className="text-left  px-2 py-1.5">status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r._id}
              onClick={() => onOpen(r.unique_id)}
              className="border-t border-slate-800 hover:bg-slate-900/50 cursor-pointer"
            >
              <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">
                {r.ts ? relativeTime(r.ts) : '—'}
              </td>
              <td className="px-2 py-1.5 font-mono text-slate-200">{r.source}</td>
              <td className="px-2 py-1.5 text-slate-200 max-w-[24rem] truncate">{r.title}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">
                {num(r.price)}
              </td>
              <td className="px-2 py-1.5 text-center text-slate-300" title={
                typeof r.discountPct === 'number' ? `${r.discountPct}% off` : 'discountPct unknown'
              }>
                {(() => {
                  const t = dealTierFromDiscount(r.discountPct);
                  return t ? <span>{DEAL_TIER_EMOJI[t]}</span> : <span className="text-slate-600">—</span>;
                })()}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">
                {num(r.dcs)}
              </td>
              <td className={cn('px-2 py-1.5 font-mono', DECISION_COLOR[r.decision ?? ''] ?? 'text-slate-400')}>
                {txt(r.decision)}
              </td>
              <td className="px-2 py-1.5 text-slate-400">
                {r.expired    ? <span className="text-amber-400">expired</span>
                : r.suspended ? <span className="text-rose-400">suspended</span>
                : r.outOfStock ? <span className="text-orange-400">OOS</span>
                : <span className="text-emerald-400">live</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DealDrawer({ uid, onClose }: { uid: string; onClose: () => void }) {
  const deal       = useQuery({ queryKey: ['deal', uid],          queryFn: () => getAdminDeal(uid) });
  const log        = useQuery({ queryKey: ['deal-log', uid],      queryFn: () => getDealPipelineLog(uid) });
  const deliveries = useQuery({ queryKey: ['deal-deliv', uid],    queryFn: () => getDealDeliveries(uid) });

  return (
    <div
      className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border-l border-slate-800 w-full max-w-3xl flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-slate-800 flex items-center justify-between gap-3">
          <div className="text-sm font-mono text-slate-300 truncate">{uid}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4 space-y-4 text-xs">
          {/* Deal record + flag */}
          <Section title="Deal record">
            {deal.isLoading && <span className="text-slate-500">Loading…</span>}
            {deal.data && (
              <>
                <DealHeader doc={deal.data.doc} />
                <FlagButton uid={uid} />
                <details className="mt-3">
                  <summary className="cursor-pointer text-slate-500">full JSON</summary>
                  <pre className="mt-2 text-[11px] bg-slate-950 border border-slate-800 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(deal.data.doc, null, 2)}
                  </pre>
                </details>
              </>
            )}
          </Section>

          {/* Pipeline log timeline */}
          <Section title={`Pipeline log (${log.data?.entries.length ?? '…'})`}>
            {log.isLoading && <span className="text-slate-500">Loading…</span>}
            {log.data && (
              log.data.entries.length === 0
                ? <div className="text-slate-500">No pipeline_logs rows for this unique_id.</div>
                : <ul className="space-y-1">
                    {log.data.entries.map(e => (
                      <li key={e._id} className="flex items-start gap-2">
                        <span className="text-slate-500 w-20 shrink-0">{relativeTime(e.ts)}</span>
                        <span className={cn('font-mono w-16 shrink-0', DECISION_COLOR[e.decision] ?? 'text-slate-400')}>
                          {e.decision}
                        </span>
                        <span className="text-slate-500 w-20 shrink-0 truncate">{e.stage}</span>
                        <span className="text-slate-300 flex-1 truncate">{txt(e.primary_reason)}</span>
                        {typeof e.dcs === 'number' && (
                          <span className="text-slate-500 tabular-nums">DCS={num(e.dcs)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
            )}
          </Section>

          {/* Deliveries */}
          <Section title={`Deliveries (${deliveries.data?.entries.length ?? '…'})`}>
            {deliveries.isLoading && <span className="text-slate-500">Loading…</span>}
            {deliveries.data && (
              deliveries.data.entries.length === 0
                ? <div className="text-slate-500">No deliveries logged for this deal.</div>
                : <ul className="space-y-1">
                    {deliveries.data.entries.map(d => (
                      <li key={d._id} className="flex items-start gap-2">
                        <span className="text-slate-500 w-20 shrink-0">{relativeTime(d.sent_at)}</span>
                        <span className="font-mono text-slate-200 w-24 shrink-0">tg:{d.tgId}</span>
                        <span className="text-slate-500">{d.channel ?? d.source ?? ''}</span>
                      </li>
                    ))}
                  </ul>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-slate-950/40 border border-slate-800 rounded p-3">
      <h2 className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">{title}</h2>
      {children}
    </section>
  );
}

function DealHeader({ doc }: { doc: Record<string, unknown> }) {
  const fields: Array<[string, unknown]> = [
    ['source',     doc.source],
    ['category',   doc.category],
    ['modelKey',   doc.modelKey],
    ['price',      doc.price],
    ['anchor £',   doc.anchorPrice],
    ['DCS',        doc.dcs],
    ['decision',   doc.decision],
    ['condition',  doc.condition],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
      {fields.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-2">
          <span className="text-slate-500">{k}</span>
          <span className="font-mono text-slate-200 truncate">
            {v === undefined || v === null ? '—' : String(v)}
          </span>
        </div>
      ))}
      {typeof doc.url === 'string' && (
        <a
          href={doc.url} target="_blank" rel="noopener noreferrer"
          className="col-span-full text-sky-400 hover:underline truncate"
        >
          {doc.url}
        </a>
      )}
    </div>
  );
}

function FlagButton({ uid }: { uid: string }) {
  const [open, setOpen]   = useState(false);
  const [reason, setReason] = useState<FlagReason>('wrong-DROP');
  const [note, setNote]   = useState('');
  const qc = useQueryClient();

  const m = useMutation({
    mutationFn: () => flagDeal(uid, { reason, note: note || undefined }),
    onSuccess:  () => {
      setOpen(false); setNote('');
      qc.invalidateQueries({ queryKey: ['flags-aggregate'] });
    },
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-3 px-2.5 py-1.5 text-xs rounded bg-rose-900/40 hover:bg-rose-900/70 text-rose-200 flex items-center gap-1"
      >
        <Flag className="w-3.5 h-3.5" />
        flag wrong decision
      </button>
    );
  }

  return (
    <div className="mt-3 border border-rose-900/40 rounded p-3 space-y-2 bg-rose-950/20">
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        reason
        <select
          value={reason}
          onChange={e => setReason(e.target.value as FlagReason)}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm"
        >
          {FLAG_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>
      <label className="text-xs text-slate-400 flex flex-col gap-1">
        note (optional, ≤500 chars)
        <textarea
          value={note}
          onChange={e => setNote(e.target.value.slice(0, 500))}
          rows={2}
          className="bg-slate-900 border border-slate-800 rounded px-2 py-1.5 text-sm font-mono"
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          onClick={() => m.mutate()}
          disabled={m.isPending}
          className="px-2.5 py-1.5 text-xs rounded bg-rose-700 hover:bg-rose-600 disabled:bg-slate-800 text-white"
        >
          {m.isPending ? 'submitting…' : 'submit flag'}
        </button>
        <button
          onClick={() => { setOpen(false); setNote(''); }}
          className="px-2.5 py-1.5 text-xs rounded text-slate-400 hover:text-white"
        >
          cancel
        </button>
        {m.isError && <span className="text-xs text-rose-300">failed — {(m.error as Error)?.message}</span>}
        {m.isSuccess && <span className="text-xs text-emerald-300">flag saved (training data only — deal unchanged)</span>}
      </div>
    </div>
  );
}

function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
      <Loader2 className="w-4 h-4 animate-spin" />
      {label}
    </div>
  );
}

function ErrorRow({ label }: { label: string }) {
  return <div className="text-sm text-rose-300 py-3">{label}</div>;
}
